import {
  Button,
  CheckCircleIcon,
  DragList,
  Flex,
  FlexProps,
  Icon,
  IconButton,
  InputContainer,
  Text,
  XMarkIcon,
} from 'components'
import { useCurrentUser } from 'components/providers'
import { isEqual } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import { IFacility } from 'types'
import { randomString, updateDefaultTrackingProperties, updateDoc } from 'utils'
import Keyboard from '../Keyboard'
import TextFieldWithKeyboard from '../TextFieldWithKeyboard'
import { useExplorerConfig } from '../providers'

interface Props extends FlexProps {
  onDirty?: (isDirty: boolean) => void
}

interface Draft extends Omit<IFacility, 'visitorNames'> {
  namesWithId: IdAndValue[]
}

interface IdAndValue {
  id: string
  value: string
}

export default function VisitorSettings({ onDirty, ...rest }: Props) {
  const user = useCurrentUser()
  const { facility } = useExplorerConfig()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [uneditedDraft, setUneditedDraft] = useState<Draft | null>(null)
  const [newVisitorName, setNewVisitorName] = useState('')

  const { companyName = '', namesWithId = [] } = draft || {}
  const hasEdits = !isEqual(draft, uneditedDraft)

  useEffect(() => {
    if (!facility) {
      setDraft(null)
      setUneditedDraft(null)
      setNewVisitorName('')
      return
    }

    const { visitorNames, ...rest } = facility
    const namesWithId = visitorNames?.map(name => ({ id: randomString(), value: name })) ?? []
    setDraft({ ...rest, namesWithId })
    setUneditedDraft({ ...rest, namesWithId })
  }, [facility])

  useEffect(() => {
    onDirty?.(hasEdits)
  }, [hasEdits])

  function update(body: Partial<Draft>) {
    if (!draft) return
    setDraft({ ...draft, ...body })
  }

  function addNew() {
    if (!isWhitespaceString(newVisitorName)) {
      const value = newVisitorName.trim()
      update({ namesWithId: [...namesWithId, { id: randomString(), value }] })
    }
    setNewVisitorName('')
  }

  async function handleSave() {
    const { namesWithId, ...rest } = draft!
    const visitorNames = namesWithId.map(({ value }) => value)
    await updateDoc(user!.id, 'facilities', { visitorNames, ...rest })
  }

  return (
    <Flex overflowY="hidden" direction="column" gap={6} {...rest}>
      <Flex overflowY="auto" direction="column" gap={6}>
        <TextFieldWithKeyboard
          disabled={!draft}
          label="Header"
          value={companyName}
          onChange={companyName => {
            updateDefaultTrackingProperties({ companyName })
            update({ companyName })
          }}
          rightElement={
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Clear header"
              icon={<XMarkIcon />}
              onClick={e => {
                e.stopPropagation()
                update({ companyName: '' })
              }}
            />
          }
        />

        <InputContainer label="Visitor Names">
          <Flex direction="column" gap={2}>
            <VisitorNames
              disabled={!draft}
              names={namesWithId}
              onChange={namesWithId => update({ namesWithId })}
            />
            <Flex align="center" gap={2} justify="space-between">
              <Icon>add</Icon>
              <TextFieldWithKeyboard
                disabled={!draft}
                placeholder="Name"
                variant="flushed"
                value={newVisitorName}
                onKeyDown={e => e.key === 'Enter' && addNew()}
                onKeyboardClose={addNew}
                onChange={v => setNewVisitorName(v)}
              />
              <IconButton
                disabled={!draft || !newVisitorName}
                variant="ghost"
                aria-label="Add name"
                size="sm"
                icon={<Icon>check</Icon>}
                onClick={addNew}
              />
            </Flex>
          </Flex>
        </InputContainer>
      </Flex>

      <Flex justify="flex-end" gap={2}>
        {hasEdits && (
          <Button variant="ghost" onClick={() => setDraft(uneditedDraft)}>
            Cancel
          </Button>
        )}
        <Button
          disabled={!hasEdits}
          variant="outline"
          leftIcon={<CheckCircleIcon type="solid" />}
          onClick={handleSave}
        >
          Save
        </Button>
      </Flex>
    </Flex>
  )
}

interface VisitorNamesProps {
  disabled?: boolean
  names: IdAndValue[]
  onChange: (names: IdAndValue[]) => void
}

function VisitorNames({ disabled, names, onChange }: VisitorNamesProps) {
  return (
    <DragList
      items={names}
      onChange={onChange}
      renderItem={(item, isDragging) => (
        <Visitor
          value={item.value}
          disabled={disabled}
          isDragging={isDragging}
          onChange={value => onChange(names.map(i => (i.id === item.id ? { ...i, value } : i)))}
          onRemove={() => onChange(names.filter(i => i.id !== item.id))}
        />
      )}
    />
  )
}

interface VisitorProps {
  disabled?: boolean
  isDragging: boolean
  value: string
  onChange: (value: string) => void
  onRemove: () => void
}

function Visitor({ disabled, isDragging, value, onRemove, onChange, ...rest }: VisitorProps) {
  const [draft, setDraft] = useState(value)
  const [openKeyboard, setOpenKeyboard] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  return (
    <Flex
      flex={1}
      gap={2}
      cursor="grab"
      align="center"
      justify="space-between"
      borderRadius={4}
      backgroundColor={isDragging ? '#ffffff33' : 'transparent'}
      overflow="hidden"
    >
      <Icon cursor="grab">drag_indicator</Icon>
      <Text
        ref={textRef}
        flex={1}
        fontSize="md"
        cursor="pointer"
        onClick={e => {
          e.stopPropagation()
          setOpenKeyboard(true)
        }}
        lineClamp={1}
      >
        {value}
      </Text>
      <IconButton
        variant="ghost"
        color="inherit"
        size="sm"
        aria-label="Remove name"
        icon={<Icon>close</Icon>}
        onClick={onRemove}
      />

      <Keyboard
        disabled={disabled}
        inputRef={textRef.current}
        open={openKeyboard}
        onClose={() => {
          setOpenKeyboard(false)
          onChange(draft)
        }}
        value={draft}
        onChange={setDraft}
        {...rest}
      />
    </Flex>
  )
}

const isWhitespaceString = (str: string) => !str.replace(/\s/g, '').length
